import {
  createScreenViewedEvent,
  createComponentClickedEvent,
  createComponentViewedEvent,
  createSubmittedEvent,
  createListScrolledEvent,
  createCardClickedEvent,
  track,
} from "./base"
import { type AnalysisListType } from "@/screens/analyze/utils/Properties"
import { type AppScreen } from "@/constants/Screens"
import { type CarouselData } from "@/components/properties/myleads/MyLeadsConnectData"

export const sortClicked = () => {
  track(
    createComponentClickedEvent({
      current_screen: "Properties",
      component_type: "icon",
      component_function: "show sort options",
    })
  )
}

export const sortByClicked = ({
  sort,
  sortValue,
}: {
  sort: string
  sortValue: string
}) => {
  track(
    createComponentClickedEvent({
      current_screen: "Properties",
      component_type: "menu_option",
      component_function: "sort",
      component_text: sort,
      clicked_values: {
        sort_value: sortValue,
      },
    })
  )
}

export const myLeadsBannerViewed = ({
  text,
  size,
  position,
}: {
  text: string
  size: string
  position: string
}) => {
  track(
    createComponentViewedEvent({
      current_screen: "Properties",
      component_type: "toast",
      component_function: "go to My Leads setup",
      component_text: text,
      component_size: size,
      component_position: position,
    })
  )
}

export const myLeadsOnboardingViewed = () => {
  track(
    createScreenViewedEvent({
      current_screen: "My Leads Onboarding",
      from_screen: "Properties",
    })
  )
}

export const myLeadsDeclinedClicked = () => {
  track(
    createComponentClickedEvent({
      current_screen: "My Leads Onboarding",
      component_type: "text",
      component_function: "decline My Leads",
      component_text: "I don't want to save time",
    })
  )
}

export const myLeadsSetupClicked = (data?: CarouselData) => {
  track(
    createComponentClickedEvent({
      current_screen: "My Leads Onboarding",
      component_type: "button",
      component_function: "setup My Leads",
      component_text: data?.ctaText || "I Want My Leads!",
    })
  )
}

export const myLeadsTermsViewed = () => {
  track(
    createScreenViewedEvent({
      current_screen: "My Leads Terms",
      current_tab: "Properties",
      from_screen: "My Leads Onboarding",
    })
  )
}

export const myLeadsAuthSubmitted = () => {
  track(
    createSubmittedEvent({
      object_name: "My Leads Auth",
      current_screen: "My Leads Terms",
      to_screen: "Ext:Google Auth Screens",
      component_type: "button",
      component_text: "Authorize with Google Account",
    })
  )
}

export const myLeadsSyncViewed = () => {
  track(
    createScreenViewedEvent({
      current_screen: "My Leads Sync",
      from_screen: "Ext:Google Auth Screens",
    })
  )
}

export const notificationActionClicked = (
  analysisId: string,
  currentScreen: AppScreen,
  enableNotifications: boolean
) => {
  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      component_type: "icon",
      component_function: !enableNotifications
        ? "disable notifications"
        : "enable notifications",
      clicked_values: {
        analysis_id: analysisId,
      },
    })
  )
}

export const favoritesActionClicked = (
  currentScreen: AppScreen,
  favorite: boolean
) => {
  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      component_type: "icon",
      component_function: !favorite ? "unfavorite" : "favorite",
    })
  )
}

export const propertiesViewed = ({
  currentTab,
}: {
  currentTab: AnalysisListType
}) => {
  track(
    createScreenViewedEvent({
      current_screen: "Properties",
      current_tab: currentTab,
    })
  )
}

export const myLeadsViewed = ({
  currentTab,
}: {
  currentTab: AnalysisListType
}) => {
  track(
    createScreenViewedEvent({
      current_screen: "My Leads",
      current_tab: currentTab,
    })
  )
}

export const propertiesListScrolled = ({
  scrolledTo,
}: {
  scrolledTo: number
}) => {
  track(
    createListScrolledEvent({
      current_screen: "Properties",
      list_type: "property",
      scrolled_to: scrolledTo,
    })
  )
}

export const propertyCardClicked = ({
  cardListNumber,
  propertyType,
  propertyAddress,
  roi,
  price,
  arv,
  profit,
  isNew,
}: {
  cardListNumber: number
  propertyType: "sourced" | "shared" | "analysis"
  propertyAddress: string
  roi?: string
  price?: number
  arv?: number
  profit?: number
  isNew: boolean
}) => {
  track(
    createCardClickedEvent({
      current_screen: "Properties",
      to_screen: "Subject Property",
      card_type: "property",
      card_list_number: cardListNumber,
      card_values: {
        property_type: propertyType,
        property_address: propertyAddress,
        deal_type: roi,
        price,
        arv,
        profit,
        is_new: isNew,
      },
    })
  )
}

export const myLeadsRedirectToAppStoresViewed = ({
  fromScreen = "Unknown",
}: {
  fromScreen?: AppScreen
}) => {
  track(
    createScreenViewedEvent({
      current_screen: "My Leads App Redirect",
      from_screen: fromScreen,
    })
  )
}

export const myLeadsPlaceholderAppInstallClicked = ({
  badgeType,
}: {
  badgeType: "apple_install_badge" | "google_install_badge"
}) => {
  track(
    createComponentClickedEvent({
      current_screen: "My Leads App Redirect",
      component_type: badgeType,
      component_function: "link to app store",
    })
  )
}
